import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useToasts } from '../../../Components/Toasts';
import Button from '../../../Components/Button/Button';
import Form, { Actions, RequiredValidator, TextField } from '../../../Components/Form';

const base64UrlToUint8Array = (base64Url) => {
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    return Uint8Array.from(binary, c => c.charCodeAt(0));
};

const PasskeyChallenge = ({ onSubmit, challenge }) => {
    const {showError} = useToasts();
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [showRecoveryCode, setShowRecoveryCode] = useState(false);
    const hasAutoTriggered = useRef(false);

    const handleAuthenticate = useCallback(async () => {
        if (isAuthenticating || !challenge?.data?.publicKey) {
            return;
        }

        setIsAuthenticating(true);

        try {
            const publicKeyCredentialRequestOptions = challenge.data.publicKey;

            const publicKeyOptions = {
                ...publicKeyCredentialRequestOptions,
                challenge: base64UrlToUint8Array(publicKeyCredentialRequestOptions.challenge),
            };

            if (publicKeyCredentialRequestOptions.allowCredentials && publicKeyCredentialRequestOptions.allowCredentials.length > 0) {
                publicKeyOptions.allowCredentials = publicKeyCredentialRequestOptions.allowCredentials.map(cred => ({
                    ...cred,
                    id: base64UrlToUint8Array(cred.id),
                }));
            }

            const credential = await navigator.credentials.get({
                publicKey: publicKeyOptions,
            });

            const response = {
                id: credential.id,
                rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))),
                response: {
                    authenticatorData: btoa(String.fromCharCode(...new Uint8Array(credential.response.authenticatorData))),
                    clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(credential.response.clientDataJSON))),
                    signature: btoa(String.fromCharCode(...new Uint8Array(credential.response.signature))),
                    userHandle: credential.response.userHandle ? btoa(String.fromCharCode(...new Uint8Array(credential.response.userHandle))) : null,
                },
                type: credential.type,
            };

            await onSubmit({
                authenticatorResponse: JSON.stringify(response),
            });
        } catch (err) {
            if (err.name === 'NotAllowedError') {
                showError('Passkey authentication was cancelled or not available');
            } else {
                showError(`Passkey authentication failed: ${err.message || err.toString()}`);
            }
        } finally {
            setIsAuthenticating(false);
        }
    }, [challenge?.data?.publicKey, isAuthenticating, onSubmit, showError]);

    useEffect(() => {
        if (challenge?.data?.publicKey && !isAuthenticating && !showRecoveryCode && !hasAutoTriggered.current) {
            hasAutoTriggered.current = true;
            handleAuthenticate();
        }
    }, [challenge?.data?.publicKey, isAuthenticating, showRecoveryCode, handleAuthenticate]);

    if (showRecoveryCode) {
        return <Form onSubmit={(data) => onSubmit({
            recoveryCode: data.recoveryCode,
            authenticatorResponse: '',
        })}>
            <TextField
                type="text"
                name="recoveryCode"
                label="Recovery code"
                placeholder="Enter recovery code"
                validators={[RequiredValidator]}
                autoComplete="one-time-code"
            />
            <Actions
                submit={{label: 'Submit'}}
                cancel={{label: 'Cancel', onClick: () => setShowRecoveryCode(false)}}
            />
        </Form>;
    }

    return (
        <div className="text-center py-4">

            <p className="text-muted mb-4">Please use your passkey to continue</p>

            <Button theme="primary" label="Use Passkey" onClick={handleAuthenticate} disabled={!challenge?.data?.publicKey} loading={isAuthenticating} />

            <p className="text-muted text-sm mt-4">
                <button type="button" onClick={() => setShowRecoveryCode(true)}>
                    Use recovery code
                </button>
            </p>
        </div>
    );
};

export default PasskeyChallenge;
